import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, Download, Eye, X, PanelLeftClose, PanelLeftOpen, Flag } from 'lucide-react';
import { SettingsToggleButton } from '@/components/ui/settings-toggle-button';
import { useSettingsVisibility } from '@/hooks/useSettingsVisibility';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { TimelineItem, ItemFilters, ActiveFunctions } from '@/types/item';
import TimelineReportSettings from './TimelineReportSettings';
import TimelineReportMilestoneFilter from './TimelineReportMilestoneFilter';
import { SummaryMilestone } from './MilestoneSummarySelector';
import { resolveSummaryMilestoneDates } from '@/utils/milestoneSummaryLayout';
import { resolveSummaryMilestones } from '@/utils/resolveSummaryMilestones';
import { useTimelineReportExport } from '@/hooks/timeline-report/useTimelineReportExport';
import { useProjectDetail } from '@/hooks/useProjectDetail';
import { useTimelineReportData } from '@/hooks/timeline-report/useTimelineReportData';
import { useProjectTeamMembers } from '@/hooks/useProjectTeamMembers';
import { useAssignedUserProfiles } from '@/hooks/useAssignedUserProfiles';
import { PLAN_TRANSLATIONS } from '@/constants/planTranslations';
import { useAppSettings } from '@/hooks/useAppSettings';
import { HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS, resolveHighlightStyle } from '@/utils/highlightStyle';

interface TimelineReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  items: TimelineItem[];
  projectId?: string;
  filters?: ItemFilters;
  activeFunctions?: ActiveFunctions;
  summaryMilestones?: SummaryMilestone[];
  onSummaryMilestonesChange?: (milestones: SummaryMilestone[]) => void;
}

const TimelineReportDialog = ({
  isOpen,
  onClose,
  items,
  projectId,
  filters,
  activeFunctions,
  summaryMilestones: propSummaryMilestones,
  onSummaryMilestonesChange
}: TimelineReportDialogProps) => {
  const { isMobile, showSettings, setShowSettings } = useSettingsVisibility({
    storageKey: 'timeline-report-settings-visible'
  });

  const [settings, setSettings] = useState<{
    pageFormat: 'portrait' | 'landscape';
    showCategoryColors: boolean;
    showCategories: boolean;
    title: string;
    titlePrefix?: string;
    sortOrder: 'current' | 'category';
    showMilestones?: boolean;
    milestoneFilters?: {
      activeMilestoneTypes?: string[];
      milestoneName?: string;
    };
    customDateRange?: {
      enabled: boolean;
      startDate: string;
      endDate: string;
    };
    stretchToFullWidth?: boolean;
    showAssessmentIssues?: boolean;
    showProjectHealth?: boolean;
    showCategoryPerformance?: boolean;
    showStatistics?: boolean;
  }>({
    pageFormat: 'landscape',
    showCategoryColors: true,
    showCategories: false,
    title: PLAN_TRANSLATIONS.reportDialogTitle as string,
    titlePrefix: 'Project Timeline',
    sortOrder: 'current',
    showMilestones: true,
    milestoneFilters: {
      activeMilestoneTypes: [],
      milestoneName: ''
    },
    customDateRange: {
      enabled: false,
      startDate: '',
      endDate: ''
    },
    stretchToFullWidth: true,
    showAssessmentIssues: false,
    showProjectHealth: false,
    showCategoryPerformance: false,
    showStatistics: false,
  });

  // Use external summaryMilestones from parent (ItemsManagement)
  const summaryMilestones = propSummaryMilestones || [];

  const { data: project } = useProjectDetail(projectId || '');
  const { data: singleProjectTeamMembers = [] } = useProjectTeamMembers(projectId || '', { enabled: !!projectId });
  
  const { exportTimelineReport, isExporting, buildTimelineHtmlDocument } = useTimelineReportExport();

  const { getSetting } = useAppSettings();
  const highlightStyle = resolveHighlightStyle(getSetting(HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS));
  
  const reportData = useTimelineReportData({
    items,
    filters,
    activeFunctions,
    selectedProject: projectId,
    pageFormat: settings.pageFormat,
    sortOrder: settings.sortOrder,
    customDateRange: settings.customDateRange?.enabled && settings.customDateRange.startDate && settings.customDateRange.endDate
      ? { startDate: settings.customDateRange.startDate, endDate: settings.customDateRange.endDate }
      : undefined,
    stretchToFullWidth: settings.stretchToFullWidth,
    showCategories: settings.showCategories
  });

  // For multi-project: fetch profiles from assigned_user_ids in items and milestones
  const { data: assignedProfiles = [] } = useAssignedUserProfiles(
    items, 
    reportData.milestonesByItem,
    !projectId // Only enable when multi-project
  );
  
  // Use single project team members when filtering 1 project, otherwise use assigned profiles from items
  const teamMembers = projectId ? singleProjectTeamMembers : assignedProfiles;

  // M1: tra lại mốc sống theo id — tên/ngày mới, mốc đã xoá biến mất
  const resolvedSummaryMilestones = useMemo(
    () =>
      resolveSummaryMilestones(summaryMilestones, {
        milestones: Object.values(reportData.milestonesByItem || {}).flat() as any[],
        items: items as any[],
      }),
    [summaryMilestones, reportData.milestonesByItem, items]
  );

  const isMultiProject = !projectId;
  const projectTitle = isMultiProject ? PLAN_TRANSLATIONS.allProjects : (project?.project_name || PLAN_TRANSLATIONS.project);

  const handleExport = async () => {
    if (!reportData.filteredItems.length) return;
    
    // Multi-project validation
    if (isMultiProject && reportData.filteredItems.length > 150) {
      toast.warning(PLAN_TRANSLATIONS.largeReport, {
        description: `${reportData.filteredItems.length} items may take time to process`
      });
    }
    
    await exportTimelineReport({
      ...reportData,
      projectName: projectTitle,
      projectImageUrl: project?.image_url || undefined,
      teamMembers,
      settings: {
        ...settings,
        highlightStyle,
        milestoneFilters: {
          activeMilestoneTypes: filters?.activeMilestoneTypes ?? settings.milestoneFilters?.activeMilestoneTypes ?? [],
          milestoneName: filters?.milestoneName ?? settings.milestoneFilters?.milestoneName ?? '',
        },
      },
      isMultiProject,
      summaryMilestones: resolveSummaryMilestoneDates(resolvedSummaryMilestones, items, reportData.milestonesByItem),
      showAssessmentIssues: settings.showAssessmentIssues,
      showProjectHealth: settings.showProjectHealth,
      showCategoryPerformance: settings.showCategoryPerformance,
      // Flatten assessment data for export
      projectHealth: reportData.assessment?.projectHealth,
      categoryPerformance: reportData.assessment?.categoryPerformance,
      issues: reportData.assessment?.issues,
    });
  };

  const handleExportPDF = () => {
    if (!reportData.filteredItems.length) return;
    
    // Multi-project validation
    if (isMultiProject && reportData.filteredItems.length > 150) {
      toast.warning(PLAN_TRANSLATIONS.largeReport, {
        description: `${reportData.filteredItems.length} items may take time to process`
      });
    }
    
    // Create preview window (without auto-print)
    const previewWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes');
    if (!previewWindow) return;
    
    // Build complete HTML with actual timeline content
    const fullHtml = buildTimelineHtmlDocument({
      ...reportData,
      projectName: projectTitle,
      projectImageUrl: project?.image_url || undefined,
      teamMembers,
      settings: {
        ...settings,
        highlightStyle,
        milestoneFilters: {
          activeMilestoneTypes: filters?.activeMilestoneTypes ?? settings.milestoneFilters?.activeMilestoneTypes ?? [],
          milestoneName: filters?.milestoneName ?? settings.milestoneFilters?.milestoneName ?? '',
        },
      },
      isMultiProject,
      summaryMilestones: resolveSummaryMilestoneDates(resolvedSummaryMilestones, items, reportData.milestonesByItem),
      showAssessmentIssues: settings.showAssessmentIssues,
      showProjectHealth: settings.showProjectHealth,
      showCategoryPerformance: settings.showCategoryPerformance,
      // Flatten assessment data for export
      projectHealth: reportData.assessment?.projectHealth,
      categoryPerformance: reportData.assessment?.categoryPerformance,
      issues: reportData.assessment?.issues,
    });
    
    // Write the complete HTML to preview window
    previewWindow.document.write(fullHtml);
    previewWindow.document.close();
    
    // NO auto-print - user clicks "In ngay" button when ready
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-3 sm:p-4 md:p-6">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {PLAN_TRANSLATIONS.reportDialogTitle} - {projectTitle}
              {isMultiProject && (
                <span className="text-xs text-muted-foreground font-normal">
                  ({reportData.statistics.projectCount || 0} {PLAN_TRANSLATIONS.projects})
                </span>
              )}
            </DialogTitle>
            <SettingsToggleButton
              showSettings={showSettings}
              onToggle={() => setShowSettings(v => !v)}
              isMobile={isMobile}
              targetId="timeline-settings-panel"
              labels={{
                hide: PLAN_TRANSLATIONS.hideSettings,
                show: PLAN_TRANSLATIONS.showSettings
              }}
            />
          </div>
        </DialogHeader>

        <div className="space-y-6">

          {/* Settings Panel */}
          <div
            id="timeline-settings-panel"
            role="complementary"
            aria-label="Timeline report settings"
            className={cn(
              "transition-all duration-200",
              !showSettings && "hidden"
            )}
          >
            <TimelineReportSettings
              settings={settings}
              onSettingsChange={setSettings}
            />
          </div>

          {/* Milestone Filter UI */}
          {settings.showMilestones && (
            <TimelineReportMilestoneFilter
              activeMilestoneTypes={settings.milestoneFilters?.activeMilestoneTypes || []}
              milestoneName={settings.milestoneFilters?.milestoneName || ''}
              onChange={(filters) => setSettings(prev => ({
                ...prev,
                milestoneFilters: filters
              }))}
            />
          )}

          {/* Milestone Summary - Compact View (selection happens in Tab Plan) */}
          {resolvedSummaryMilestones.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 text-sm">
                <Flag className="h-4 w-4 text-amber-600" />
                <span className="font-medium">{resolvedSummaryMilestones.length} milestones đã chọn cho Timeline Tổng quan</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Điều chỉnh trong Tab Plan để thay đổi selection
              </p>
            </div>
          )}

          {/* Page Format Info */}
          <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 dark:text-blue-100">
                  {PLAN_TRANSLATIONS.optimizedForLongTimeline}
                </p>
                <p className="text-blue-700 dark:text-blue-300 mt-1">
                  {settings.pageFormat === 'landscape' 
                    ? `Landscape A4: ~${Math.floor(800/6)}-${Math.floor(800/8)} days/page, optimized for long timelines (e.g., Penthouse Sunrise City: 113 days → ${reportData.pages.length} pages)`
                    : `Portrait A4: ~${Math.floor(600/12)} days/page, suitable for short timelines`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={handleExportPDF}
              disabled={isExporting || reportData.filteredItems.length === 0}
              className="w-full"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  {PLAN_TRANSLATIONS.opening}
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  {PLAN_TRANSLATIONS.exportPDF}
                </>
              )}
            </Button>
          </div>

          {reportData.filteredItems.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{PLAN_TRANSLATIONS.noItemsToReport}</p>
              <p className="text-sm mt-1">{PLAN_TRANSLATIONS.checkFilters}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimelineReportDialog;